import { Constants } from "@eagerdog/constants";
import { RegistrationOption } from "./types";

/**
 * Registration number type lists. These match the options DogForm offers
 * for each sanctioning club.
 */
export const registrationTypeOptions: Record<string, RegistrationOption[]> = {
  [Constants.sanctioning_club.UKC]: [
    { id: Constants.registration_number_type.ukc_registered, value: Constants.registration_number_type.ukc_permanent },
    { id: Constants.registration_number_type.ukc_temporary, value: Constants.registration_number_type.ukc_temporary },
    { id: Constants.registration_number_type.ukc_performance, value: Constants.registration_number_type.ukc_performance_pl },
  ],
  [Constants.sanctioning_club.AKC]: [
    { id: Constants.registration_number_type.akc_registered, value: Constants.registration_number_type.akc_registered },
    { id: Constants.registration_number_type.akc_pal, value: Constants.registration_number_type.akc_pal },
    { id: Constants.registration_number_type.akc_canine_partner, value: Constants.registration_number_type.akc_canine_partner },
    { id: Constants.registration_number_type.akc_foreign_reg, value: Constants.registration_number_type.akc_foreign_reg },
  ],
  [Constants.sanctioning_club.CKC]: [
    { id: Constants.registration_number_type.ckc_registered, value: Constants.registration_number_type.ckc_registered },
    { id: Constants.registration_number_type.ckc_ws, value: Constants.registration_number_type.ckc_ws },
    { id: Constants.registration_number_type.ckc_msc, value: Constants.registration_number_type.ckc_msc },
    { id: Constants.registration_number_type.ckc_ern, value: Constants.registration_number_type.ckc_ern },
    { id: Constants.registration_number_type.ckc_ccn, value: Constants.registration_number_type.ckc_ccn },
    { id: Constants.registration_number_type.ckc_pen, value: Constants.registration_number_type.ckc_pen },
    { id: Constants.registration_number_type.ckc_tcn, value: Constants.registration_number_type.ckc_tcn },
  ],
  [Constants.sanctioning_club.ABI]: [
    { id: Constants.sanctioning_club.UKC, value: Constants.registration_number_type.abi_registered },
  ],
  [Constants.sanctioning_club.ISWS]: [
    { id: Constants.registration_number_type.isws_registered, value: Constants.registration_number_type.isws_registered },
  ],
  [Constants.sanctioning_club.PWDCA]: [
    { id: Constants.sanctioning_club.AKC, value: Constants.sanctioning_club.AKC },
    { id: Constants.sanctioning_club.CKC, value: Constants.sanctioning_club.CKC },
  ],
  [Constants.sanctioning_club.CPE]: [
    { id: Constants.sanctioning_club.CPE, value: Constants.sanctioning_club.CPE },
  ],
  [Constants.sanctioning_club.GCR]: [
    { id: Constants.sanctioning_club.GCR, value: Constants.sanctioning_club.GCR },
  ],
  [Constants.sanctioning_club.GENERAL]: [
    { id: Constants.sanctioning_club.GENERAL, value: Constants.sanctioning_club.GENERAL },
  ],
  [Constants.sanctioning_club.C_WAGS]: [
    { id: Constants.sanctioning_club.C_WAGS, value: Constants.sanctioning_club.C_WAGS },
  ],
  [Constants.sanctioning_club.BHA]: [
    { id: Constants.registration_number_type.barn_hunt, value: Constants.registration_number_type.barn_hunt },
  ],
  [Constants.sanctioning_club.ASFA]: [
    { id: Constants.registration_number_type.asfa_registered, value: Constants.registration_number_type.asfa_registered },
  ],
  [Constants.sanctioning_club.FH]: [],
  [Constants.sanctioning_club.AHBA]: [
    { id: Constants.registration_number_type.ahba_registered, value: Constants.registration_number_type.ahba_registered },
  ],
};

/**
 * CKC conformation accepts a dog registered with the CKC, holding an Event Registration
 * Number, a Temporary Competition Number, or (listed breeds) a Miscellaneous Certification
 * Number (CKC Conformation Show Rules 8.2.1). CCN and PEN are performance-only numbers.
 */
export const CKC_CONFORMATION_REGISTRATION_TYPES: string[] = [
  Constants.registration_number_type.ckc_registered,
  Constants.registration_number_type.ckc_ern,
  Constants.registration_number_type.ckc_msc,
  Constants.registration_number_type.ckc_tcn,
];

/**
 * CKC obedience accepts the six number types on the official entry form: CKC registration,
 * ERN, MSC, PEN, CCN, and TCN (CKC Obedience Trial Rules 6.1.1). WS is not an obedience number.
 */
export const CKC_OBEDIENCE_REGISTRATION_TYPES: string[] = [
  Constants.registration_number_type.ckc_registered,
  Constants.registration_number_type.ckc_ern,
  Constants.registration_number_type.ckc_msc,
  Constants.registration_number_type.ckc_pen,
  Constants.registration_number_type.ckc_ccn,
  Constants.registration_number_type.ckc_tcn,
];

/** CKC number types accepted per show type. Show types not listed accept every CKC option. */
export const CKC_REGISTRATION_TYPES_BY_SHOW_TYPE: Record<string, string[]> = {
  [Constants.show_type.conformation]: CKC_CONFORMATION_REGISTRATION_TYPES,
  [Constants.show_type.obedience]: CKC_OBEDIENCE_REGISTRATION_TYPES,
  // Rally entries follow the obedience entry requirements (CKC Rally Obedience Trial Rules 6.1.1)
  [Constants.show_type.rally_obedience]: CKC_OBEDIENCE_REGISTRATION_TYPES,
  // Scent Detection lists the same six number types (CKC Scent Detection Trial Rules 6.1.1)
  [Constants.show_type.scent_detection]: CKC_OBEDIENCE_REGISTRATION_TYPES,
};

/** Registration type options narrowed to what the show accepts. */
export const registrationTypeOptionsFor = (sanctioningClub: string, showType?: string): RegistrationOption[] => {
  const options = registrationTypeOptions[sanctioningClub] || [];
  if (sanctioningClub === Constants.sanctioning_club.CKC && showType) {
    const accepted = CKC_REGISTRATION_TYPES_BY_SHOW_TYPE[showType];
    if (accepted) {
      return options.filter((option) => accepted.includes(option.id));
    }
  }
  return options;
};

export const isPalRegistrationType = (registrationType?: string) => {
  return registrationType === Constants.registration_number_type.akc_pal
    || registrationType === Constants.registration_number_type.akc_canine_partner;
};
